import React, { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faSearch,
	faTimes,
	faChevronLeft,
	faChevronRight,
	faAngleDoubleLeft,
	faAngleDoubleRight,
} from "@fortawesome/free-solid-svg-icons"
import {
	fetchEntites,
	setEntitesListUi,
} from "../../../store/actions/entiteActions"
import {
	ENTITE_TYPE_SOCIETE,
	ENTITE_TYPE_PERSONNE_PHYSIQUE,
} from "../entite/Entite"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { tableStyles } from "../../styles/tableStyles"
import CustomModal from "../../basics/interface/CustomModal"
import Loader from "../../basics/Loader"
import ClientToAtelierModalV2 from "./ClientToAtelierModalV2"
import ClientToLocationModalV2 from "./ClientToLocationModalV2"
import ClientToRdvModalV2 from "./ClientToRdvModalV2"

const PAGE_SIZE = 20

// Filtres de type : clients = sociétés + personnes physiques.
const TYPE_FILTERS = [
	{ key: "all", label: "Tous" },
	{ key: ENTITE_TYPE_SOCIETE, label: "Sociétés" },
	{ key: ENTITE_TYPE_PERSONNE_PHYSIQUE, label: "Particuliers" },
]

// Particulier → Nom (gras) Prénom ; société → Label (gras).
const clientNameNode = (ent) => {
	if (ent.type === ENTITE_TYPE_PERSONNE_PHYSIQUE) {
		const nom = ent.nom || ""
		const prenom = ent.prenom || ""
		if (!nom && !prenom) return ent.label || "—"
		return (
			<>
				<strong style={{ fontWeight: 600 }}>{nom}</strong>
				{prenom ? ` ${prenom}` : ""}
			</>
		)
	}
	return <strong style={{ fontWeight: 600 }}>{ent.label || "—"}</strong>
}

const typeBadge = (type) =>
	type === ENTITE_TYPE_SOCIETE
		? { label: "Société", color: COLORS.accent, bg: COLORS.accentLight }
		: { label: "Particulier", color: COLORS.success, bg: COLORS.successLight }

const styles = {
	header: {
		display: "flex",
		alignItems: "flex-end",
		justifyContent: "space-between",
		gap: 16,
		marginBottom: 18,
		flexWrap: "wrap",
	},
	toolbar: {
		display: "flex",
		alignItems: "center",
		gap: 12,
		flexWrap: "wrap",
	},
	searchWrap: { position: "relative", width: 240, maxWidth: "60vw" },
	clearBtn: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		width: 34,
		height: 34,
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.textMuted,
		cursor: "pointer",
		fontSize: 13,
		flexShrink: 0,
	},
	searchIcon: {
		position: "absolute",
		left: 13,
		top: "50%",
		transform: "translateY(-50%)",
		color: COLORS.textLight,
		fontSize: 13,
		pointerEvents: "none",
	},
	searchInput: {
		width: "100%",
		padding: "9px 12px 9px 36px",
		border: `1px solid ${COLORS.border}`,
		borderRadius: 10,
		fontSize: 13,
		color: COLORS.text,
		backgroundColor: COLORS.surface,
		outline: "none",
		fontFamily: "inherit",
		boxSizing: "border-box",
		transition: "border-color 0.15s",
	},
	empty: {
		...tableStyles.empty,
		padding: "48px 16px",
		color: COLORS.textLight,
		fontSize: 14,
	},
	loading: {
		padding: "48px 16px",
		textAlign: "center",
		color: COLORS.textLight,
	},
	pagination: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 8,
		marginTop: 16,
	},
	pageBtn: (disabled) => ({
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		width: 34,
		height: 34,
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: disabled ? COLORS.textLight : COLORS.text,
		cursor: disabled ? "default" : "pointer",
		opacity: disabled ? 0.45 : 1,
		fontSize: 13,
		transition: "background-color 0.12s",
	}),
	pageInfo: {
		fontSize: 13,
		color: COLORS.textMuted,
		minWidth: 92,
		textAlign: "center",
	},
	newModalRow: {
		display: "flex",
		gap: 14,
		justifyContent: "center",
		flexWrap: "wrap",
	},
	newChoice: {
		flex: 1,
		minWidth: 150,
		padding: "22px 16px",
		borderRadius: 12,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.text,
		fontSize: 14,
		fontWeight: 600,
		fontFamily: "inherit",
		cursor: "pointer",
		transition: "border-color 0.15s, background-color 0.15s",
	},
	rowBtn: {
		padding: "5px 10px",
		borderRadius: 7,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.text,
		fontSize: 12,
		fontWeight: 500,
		fontFamily: "inherit",
		cursor: "pointer",
		whiteSpace: "nowrap",
	},
}

const ClientRow = ({
	ent,
	index,
	matchTerm,
	onOpen,
	onAtelier,
	onLocation,
	onRdv,
}) => {
	const [hover, setHover] = useState(false)
	const bg = hover ? tableStyles.rowHover : tableStyles.rowBg(index)
	const badge = typeBadge(ent.type)
	// Plaque correspondant au terme recherché (terme appliqué, pas la frappe en
	// cours) → affichée sous le nom pour indiquer pourquoi le client ressort.
	const matchedPlaque = useMemo(() => {
		if (!matchTerm) return null
		const term = matchTerm.toLowerCase()
		const veh = (ent.vehicules || []).find(
			(v) => v.plaques && v.plaques.toLowerCase().includes(term),
		)
		return veh ? veh.plaques : null
	}, [ent.vehicules, matchTerm])
	return (
		<tr
			onDoubleClick={() => onOpen(ent)}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				backgroundColor: bg,
				cursor: "pointer",
				transition: "background-color 0.12s",
			}}
		>
			<td style={tableStyles.td}>
				{clientNameNode(ent)}
				{matchedPlaque && (
					<div
						style={{
							fontSize: 12,
							color: COLORS.textMuted,
							marginTop: 2,
						}}
					>
						🚗 {matchedPlaque}
					</div>
				)}
			</td>
			<td style={tableStyles.td}>
				<span style={gs.badge(badge.color, badge.bg)}>{badge.label}</span>
			</td>
			<td style={tableStyles.td}>{ent.telephone || "—"}</td>
			<td style={{ ...tableStyles.td, color: COLORS.textMuted }}>
				{ent.email || "—"}
			</td>
			<td style={tableStyles.td}>
				<div style={{ display: "flex", gap: 6 }}>
					<button
						style={styles.rowBtn}
						onClick={(e) => {
							e.stopPropagation()
							onRdv(ent)
						}}
					>
						Rdv
					</button>
					<button
						style={styles.rowBtn}
						onClick={(e) => {
							e.stopPropagation()
							onAtelier(ent)
						}}
					>
						Atelier
					</button>
					<button
						style={styles.rowBtn}
						onClick={(e) => {
							e.stopPropagation()
							onLocation(ent)
						}}
					>
						Location
					</button>
				</div>
			</td>
		</tr>
	)
}

const ClientsTableV2 = () => {
	const dispatch = useDispatch()
	const history = useHistory()
	const { entites, entitesPagination, fetchEntites: fetchState, listUi } =
		useSelector((s) => s.entiteInfo)

	// État UI initialisé depuis le store → restauré au retour d'une fiche.
	const [search, setSearch] = useState(listUi.search)
	// Terme qui a réellement produit les résultats affichés (≠ terme en cours de
	// frappe pendant la fenêtre de debounce). Sert au surlignage de la plaque.
	const [appliedSearch, setAppliedSearch] = useState(listUi.search)
	const [typeFilter, setTypeFilter] = useState(listUi.typeFilter)
	const [newOpen, setNewOpen] = useState(false)
	const [atelierFor, setAtelierFor] = useState(null) // entite | null
	const [locationFor, setLocationFor] = useState(null) // entite | null
	const [rdvFor, setRdvFor] = useState(null) // entite | null

	const buildParams = (limit = PAGE_SIZE, offset = 0) => {
		const params = { offset, limit }
		if (typeFilter === "all") {
			params.type__in = `${ENTITE_TYPE_SOCIETE},${ENTITE_TYPE_PERSONNE_PHYSIQUE}`
		} else {
			params.type = typeFilter
		}
		// `search` : recherche par nom/label OU plaque d'un véhicule (cf. EntiteView backend).
		if (search) params.search = search
		return params
	}

	const isFirstRender = useRef(true)
	const prevType = useRef(listUi.typeFilter)

	// Montage : restaure la page mémorisée et rafraîchit la liste (infos à jour
	// après une éventuelle édition de client).
	useEffect(() => {
		dispatch(fetchEntites(buildParams(PAGE_SIZE, listUi.offset)))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Saisie / changement de filtre : persiste l'état UI (offset remis à 0) puis
	// refetch avec un debounce temporel de 250 ms (une requête par pause de frappe,
	// pas par caractère). Plus de seuil de longueur → la liste reflète toujours le
	// terme tapé (pas de résultats périmés). Ignoré au 1er rendu.
	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false
			return
		}
		prevType.current = typeFilter
		dispatch(setEntitesListUi({ search, typeFilter, offset: 0 }))
		const t = setTimeout(() => {
			setAppliedSearch(search)
			dispatch(fetchEntites(buildParams(PAGE_SIZE, 0)))
		}, 250)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search, typeFilter])

	const goTo = (node) => {
		if (node) {
			dispatch(setEntitesListUi({ offset: node.offset }))
			dispatch(fetchEntites(buildParams(node.limit, node.offset)))
		}
	}

	// Lancement explicite (touche Entrée) : ignore le debounce.
	const runSearch = () => {
		dispatch(setEntitesListUi({ search, typeFilter, offset: 0 }))
		setAppliedSearch(search)
		dispatch(fetchEntites(buildParams(PAGE_SIZE, 0)))
	}

	const openClient = (ent) => history.push(`/clients/${ent.id}`)
	const createClient = (type) => {
		setNewOpen(false)
		history.push(`/clients/nouveau?type=${type}`)
	}

	const pag = entitesPagination || {}
	const totalPages = pag.last
		? Math.floor(pag.last.offset / pag.last.limit) + 1
		: pag.current || 1
	const loading = fetchState.loading

	return (
		<div style={gs.page}>
			<div style={{ width: "100%", maxWidth: 1080, margin: "0 auto" }}>
				<div style={{ marginBottom: 22 }}>
					<h2 style={gs.title}>Clients</h2>
					<div
						style={{
							fontSize: 14,
							color: COLORS.textMuted,
							marginTop: 6,
						}}
					>
						Base de données clients
					</div>
				</div>

				<div style={styles.header}>
					<div style={styles.toolbar}>
						<div style={{ ...gs.segmented, marginBottom: 0 }}>
							{TYPE_FILTERS.map((t) => (
								<button
									key={String(t.key)}
									type="button"
									style={gs.segment(typeFilter === t.key)}
									onClick={() => setTypeFilter(t.key)}
								>
									{t.label}
								</button>
							))}
						</div>
						<div style={styles.searchWrap}>
							<FontAwesomeIcon
								icon={faSearch}
								style={styles.searchIcon}
							/>
							<input
								style={styles.searchInput}
								placeholder="Rechercher…"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") runSearch()
								}}
								onFocus={(e) =>
									(e.target.style.borderColor =
										COLORS.borderStrong)
								}
								onBlur={(e) =>
									(e.target.style.borderColor = COLORS.border)
								}
							/>
						</div>
						{search && (
							<button
								type="button"
								style={styles.clearBtn}
								onClick={() => setSearch("")}
								title="Effacer la recherche"
							>
								<FontAwesomeIcon icon={faTimes} />
							</button>
						)}
					</div>
					<button
						style={gs.btnPrimary}
						onClick={() => setNewOpen(true)}
					>
						+ Nouveau client
					</button>
				</div>

				<div style={tableStyles.container}>
					{loading ? (
						<Loader variant="ring" message="Chargement des clients…" />
					) : entites.length === 0 ? (
						<div style={styles.empty}>Aucun client trouvé.</div>
					) : (
						<table style={tableStyles.table}>
							<thead>
								<tr>
									<th style={tableStyles.th}>Nom</th>
									<th style={tableStyles.th}>Type</th>
									<th style={tableStyles.th}>Téléphone</th>
									<th style={tableStyles.th}>E-mail</th>
									<th style={tableStyles.th}>Actions</th>
								</tr>
							</thead>
							<tbody>
								{entites.map((ent, i) => (
									<ClientRow
										key={ent.id}
										ent={ent}
										index={i}
										matchTerm={appliedSearch}
										onOpen={openClient}
										onAtelier={setAtelierFor}
										onLocation={setLocationFor}
										onRdv={setRdvFor}
									/>
								))}
							</tbody>
						</table>
					)}
				</div>

				{!loading && entites.length > 0 && (
					<div style={styles.pagination}>
						<button
							style={styles.pageBtn(!pag.first)}
							disabled={!pag.first}
							onClick={() => goTo(pag.first)}
							title="Première page"
						>
							<FontAwesomeIcon icon={faAngleDoubleLeft} />
						</button>
						<button
							style={styles.pageBtn(!pag.previous)}
							disabled={!pag.previous}
							onClick={() => goTo(pag.previous)}
							title="Page précédente"
						>
							<FontAwesomeIcon icon={faChevronLeft} />
						</button>
						<span style={styles.pageInfo}>
							{`Page ${pag.current || 1} / ${totalPages}`}
						</span>
						<button
							style={styles.pageBtn(!pag.next)}
							disabled={!pag.next}
							onClick={() => goTo(pag.next)}
							title="Page suivante"
						>
							<FontAwesomeIcon icon={faChevronRight} />
						</button>
						<button
							style={styles.pageBtn(!pag.last)}
							disabled={!pag.last}
							onClick={() => goTo(pag.last)}
							title="Dernière page"
						>
							<FontAwesomeIcon icon={faAngleDoubleRight} />
						</button>
					</div>
				)}
			</div>

			{newOpen && (
				<CustomModal
					title="Nouveau client"
					onClose={() => setNewOpen(false)}
					buttons={[
						{
							title: "Annuler",
							styleName: "secondary",
							onClick: () => setNewOpen(false),
						},
					]}
				>
					<p
						style={{
							margin: "0 0 18px",
							fontSize: 13,
							color: COLORS.textMuted,
							textAlign: "center",
						}}
					>
						Quel type de client souhaitez-vous créer ?
					</p>
					<div style={styles.newModalRow}>
						<button
							style={styles.newChoice}
							onClick={() => createClient(ENTITE_TYPE_SOCIETE)}
							onMouseEnter={(e) =>
								(e.currentTarget.style.borderColor =
									COLORS.accent)
							}
							onMouseLeave={(e) =>
								(e.currentTarget.style.borderColor =
									COLORS.border)
							}
						>
							Société
						</button>
						<button
							style={styles.newChoice}
							onClick={() =>
								createClient(ENTITE_TYPE_PERSONNE_PHYSIQUE)
							}
							onMouseEnter={(e) =>
								(e.currentTarget.style.borderColor =
									COLORS.accent)
							}
							onMouseLeave={(e) =>
								(e.currentTarget.style.borderColor =
									COLORS.border)
							}
						>
							Personne physique
						</button>
					</div>
				</CustomModal>
			)}

			{atelierFor && (
				<ClientToAtelierModalV2
					entite={atelierFor}
					onClose={() => setAtelierFor(null)}
				/>
			)}
			{locationFor && (
				<ClientToLocationModalV2
					entite={locationFor}
					onClose={() => setLocationFor(null)}
				/>
			)}
			{rdvFor && (
				<ClientToRdvModalV2
					entite={rdvFor}
					onClose={() => setRdvFor(null)}
				/>
			)}
		</div>
	)
}

export default ClientsTableV2
